import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { message } from 'antd';
import FormOutlined from '@ant-design/icons/FormOutlined';
import HistoryOutlined from '@ant-design/icons/HistoryOutlined';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  createInquiry,
  deleteInquiry,
  getInquiries,
  renameInquiry,
} from '@/api/inquiries';
import { setOrganizationView } from '@/api/me';
import { takePrefetchedInquiries } from '@/features/shell/bootPrefetch';
import { ACTIVE_RUN_STATUSES, cancelRun, findActiveRunId } from '@/api/runs';
import { useAppContext } from '@/context';
import { useAnnounce } from '@/features/shell/LiveRegion';
import { useBreakpoints } from '@/features/shell/useViewport';
import ChatWindow from './ChatWindow';
import { linkedInquiryId } from './deepLink';
import type { ExportRequest } from './export/ExportDialog';
import {
  DeleteInquiryDialog,
  RenameInquiryDialog,
} from './history/InquiryDialogs';
import InquiryTitle, { type InquiryTitleHandle } from './history/InquiryTitle';
import RecentQAPanel, { type RecentInquiry } from './history/RecentQAPanel';
import { moveInquiryToTop } from './moveInquiryToTop';
import { loadCurrentUserId } from './runs/runOwner';
import { runStore, useRunStore } from './runs/runStore';
import styles from './chatPage.module.css';

const RECENT_PANEL_ID = 'recent-qa-panel';

/** How often a lost run is asked about while the user stays on the page. */
const LOST_RUN_RECHECK_MS = 30_000;

// Loaded on the first export.
const ExportDialog = lazy(() => import('./export/ExportDialog'));

/** The Q&A workspace: toolbar, recent Q&As overlay and the chat of the selected Q&A. */
const ChatPage = ({ reloadData }: { reloadData: boolean }) => {
  const { t } = useTranslation();
  const { user, setUser } = useAppContext();
  const announce = useAnnounce();
  const runs = useRunStore();
  const { compact } = useBreakpoints();
  const [messageApi, contextHolder] = message.useMessage();
  const [searchParams] = useSearchParams();
  const [inquiries, setInquiries] = useState<RecentInquiry[]>([]);
  const [selectedInquiryId, setSelectedInquiryId] = useState<
    string | undefined
  >(() => linkedInquiryId(searchParams));
  const [recentOpen, setRecentOpen] = useState(false);
  // Below 640 px Recent Q&As is a modal drawer; dialogs opened from it add
  // no second surround.
  const recentModal = compact && recentOpen;
  const [renaming, setRenaming] = useState<RecentInquiry | null>(null);
  const [deleting, setDeleting] = useState<RecentInquiry | null>(null);
  const [activity, setActivity] = useState<Record<string, number>>({});
  const [exporting, setExporting] = useState<ExportRequest | null>(null);
  const [exportMounted, setExportMounted] = useState(false);
  const openExport = (request: ExportRequest) => {
    setExportMounted(true);
    setExporting(request);
  };
  const recentToggle = useRef<HTMLButtonElement>(null);
  const title = useRef<InquiryTitleHandle>(null);
  const autoSelect = useRef(!linkedInquiryId(searchParams));
  const inquiriesRef = useRef(inquiries);
  inquiriesRef.current = inquiries;

  const orgView = !!user?.is_organization_view;
  const isAdmin = user?.is_admin === true;

  /** A run the list showed as active has ended: drop its badge. */
  const clearActiveRun = useCallback((runId: string) => {
    setInquiries(previous =>
      previous.map(item =>
        item.active_run?.id === runId ? { ...item, active_run: null } : item,
      ),
    );
  }, []);

  const loadInquiries = useCallback(async () => {
    // Who the user is decides which runs are theirs (by user id, on any
    // device); without it the browser's own runs are used.
    // The first load takes over the request started at boot (D-06).
    const [response] = await Promise.all([
      takePrefetchedInquiries() ?? getInquiries({ projects_id: '' }),
      loadCurrentUserId(),
    ]);
    if (!response.success) return;
    const list = response.data as RecentInquiry[];
    setInquiries(list);
    runStore.noteListed(
      list.flatMap(item => (item.active_run ? [item.active_run] : [])),
    );
    runStore.recheckLost();
    // Runs still active after a reload (or started in another tab or
    // device): follow them, so the Generating and New badges are right in
    // every Q&A. Colleagues' Q&As (organization view) are not followed
    // unless the run is the user's own: their badge comes from the list.
    for (const item of list) {
      const runId = item.active_run?.id;
      if (!runId || runStore.getRun(runId)) continue;
      if (item.is_own === false && !runStore.isOwn(runId)) continue;
      void runStore.attach(runId, item.id).then(details => {
        if (details && !ACTIVE_RUN_STATUSES.has(details.status)) {
          clearActiveRun(runId);
        }
      });
    }
  }, [clearActiveRun]);

  // The list is not pushed (contract §15): load it again when the user
  // comes back to the tab, and every 30 s while it shows runs not followed
  // here (a colleague's, or one whose pick-up failed).
  // A lost run may have ended meanwhile: ask again when the user comes
  // back, when the network returns, and after each list load.
  useEffect(() => {
    const onFocus = () => {
      runStore.recheckLost();
      void loadInquiries();
    };
    const onOnline = () => runStore.recheckLost();
    // And every 30 s while the window has focus and is online, for a lost
    // run that ends while the user keeps reading.
    const timer = setInterval(() => {
      if (document.hasFocus() && navigator.onLine) runStore.recheckLost();
    }, LOST_RUN_RECHECK_MS);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [loadInquiries]);

  const watchesOthers = inquiries.some(
    item => item.active_run && !runStore.getRun(item.active_run.id),
  );
  useEffect(() => {
    if (!watchesOthers) return;
    const timer = setInterval(() => void loadInquiries(), 30_000);
    return () => clearInterval(timer);
  }, [loadInquiries, watchesOthers]);

  useEffect(() => {
    void loadInquiries();
  }, [loadInquiries]);

  // Without ?inquiry_id= the newest Q&A opens, as before.
  useEffect(() => {
    if (autoSelect.current && !selectedInquiryId && inquiries.length > 0) {
      autoSelect.current = false;
      setSelectedInquiryId(String(inquiries[0].id));
    }
  }, [inquiries, selectedInquiryId]);

  useEffect(() => {
    runStore.setActive(selectedInquiryId);
    // Off the Q&A page no Q&A is on screen: completions there are New.
    return () => runStore.setActive(null);
  }, [selectedInquiryId]);

  const setOrgView = async (value: boolean) => {
    setUser((prev: any) => ({ ...prev, is_organization_view: value }));
    try {
      await setOrganizationView({ is_organization_view: value });
      await loadInquiries();
    } catch (e) {
      console.error('Failed to update organization view setting', e);
      setUser((prev: any) => ({ ...prev, is_organization_view: !value }));
    }
  };

  const nameOf = (id: string | number) =>
    inquiriesRef.current.find(i => String(i.id) === String(id))?.name ?? '';

  /** The server accepted a question: the Q&A moves to the top. */
  const handleMessageSent = useCallback((inquiryId: string | number) => {
    setActivity(prev => ({ ...prev, [String(inquiryId)]: Date.now() }));
    setInquiries(prev => moveInquiryToTop(prev, inquiryId));
  }, []);

  // An answer completed in a Q&A that is not open: it is marked New.
  useEffect(
    () =>
      runStore.onFinished((run, unread) => {
        clearActiveRun(run.runId);
        if (unread) {
          announce(t('v2.history.answerReady', { q: nameOf(run.inquiryId) }));
        }
      }),
    // nameOf reads a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [announce, clearActiveRun, t],
  );

  const select = (inquiry: RecentInquiry) =>
    setSelectedInquiryId(String(inquiry.id));

  /** New Q&A: create it untitled, open it, and let the user type its title. */
  const newInquiry = async () => {
    const response: any = await createInquiry(
      { name: t('v2.history.untitled') },
      { project_id: {} },
    );
    if (!response?.success || !response?.data) {
      messageApi.error(response?.error || t('v2.history.createFailed'));
      return;
    }
    setInquiries(prev => [response.data, ...prev]);
    setSelectedInquiryId(String(response.data.id));
    const createdName = String(response.data.name ?? '');
    requestAnimationFrame(() => title.current?.edit(createdName));
  };

  const rename = async (
    inquiry: RecentInquiry,
    name: string,
  ): Promise<boolean> => {
    const response: any = await renameInquiry(String(inquiry.id), { name });
    if (!response?.success) {
      messageApi.error(response?.error || t('v2.history.renameFailed'));
      return false;
    }
    const renamed = { ...inquiry, ...(response.data ?? {}), name };
    setInquiries(prev => [
      renamed,
      ...prev.filter(item => String(item.id) !== String(inquiry.id)),
    ]);
    announce(t('v2.history.renamed'));
    return true;
  };

  /** Deleting a generating Q&A stops its run first (spec §3). */
  const remove = async (inquiry: RecentInquiry): Promise<boolean> => {
    const id = String(inquiry.id);
    if (runStore.get(id).generating || inquiry.active_run) {
      const runId =
        runStore.get(id).runId ??
        inquiry.active_run?.id ??
        (await findActiveRunId(id));
      if (runId) {
        const cancelled = await cancelRun(runId);
        if (cancelled.failed) {
          messageApi.error(t('v2.history.stopFailed'));
          return false;
        }
      }
    }
    const response: any = await deleteInquiry(id);
    if (!response?.success) {
      messageApi.error(response?.error || t('v2.history.deleteFailed'));
      return false;
    }
    runStore.forget(id);
    const remaining = inquiriesRef.current.filter(
      item => String(item.id) !== id,
    );
    setInquiries(remaining);
    if (String(selectedInquiryId) === id) {
      setSelectedInquiryId(
        remaining.length > 0 ? String(remaining[0].id) : undefined,
      );
    }
    announce(t('v2.history.deletedConv', { q: inquiry.name ?? '' }));
    return true;
  };

  const selected = inquiries.find(
    i => String(i.id) === String(selectedInquiryId),
  );
  const canEditSelected = !!selected && (!orgView || selected.is_own === true);

  const leading = (
    <>
      <button
        ref={recentToggle}
        type="button"
        className={styles.toolButton}
        aria-expanded={recentOpen}
        aria-controls={RECENT_PANEL_ID}
        aria-label={
          recentOpen ? t('v2.history.recentClose') : t('v2.history.recentOpen')
        }
        title={t('v2.history.recent')}
        onClick={() => setRecentOpen(open => !open)}
        data-testid="recent-toggle"
      >
        <HistoryOutlined aria-hidden />
      </button>
      <button
        type="button"
        className={styles.toolButton}
        aria-label={t('v2.history.newQA')}
        title={t('v2.history.newQA')}
        onClick={() => void newInquiry()}
        data-testid="new-qa"
      >
        <FormOutlined aria-hidden />
      </button>
      <InquiryTitle
        ref={title}
        name={selected?.name}
        disabled={!canEditSelected}
        onRename={name =>
          selected ? rename(selected, name) : Promise.resolve(false)
        }
      />
    </>
  );

  return (
    <div className={styles.page}>
      <ChatWindow
        selectedInquiryId={selectedInquiryId}
        reloadData={reloadData}
        onMessageSent={handleMessageSent}
        toolbarLeading={leading}
        onExport={openExport}
      />
      <div id={RECENT_PANEL_ID}>
        <RecentQAPanel
          open={recentOpen}
          modal={compact}
          onClose={() => setRecentOpen(false)}
          returnFocusRef={recentToggle}
          inquiries={inquiries}
          selectedId={selectedInquiryId}
          activity={activity}
          onSelect={select}
          onNew={() => void newInquiry()}
          onRename={setRenaming}
          onExport={inquiry =>
            openExport({
              inquiryId: String(inquiry.id),
              title: inquiry.name ?? undefined,
            })
          }
          onDelete={setDeleting}
          isAdmin={isAdmin}
          orgView={orgView}
          onOrgViewChange={value => void setOrgView(value)}
        />
      </div>
      <RenameInquiryDialog
        open={!!renaming}
        nested={recentModal}
        initialName={renaming?.name ?? ''}
        onSave={name =>
          renaming ? rename(renaming, name) : Promise.resolve(false)
        }
        onClose={() => setRenaming(null)}
      />
      <DeleteInquiryDialog
        open={!!deleting}
        nested={recentModal}
        name={deleting?.name ?? ''}
        generating={
          !!deleting &&
          (runs.get(deleting.id).generating || !!deleting.active_run)
        }
        onConfirm={() => (deleting ? remove(deleting) : Promise.resolve(false))}
        onClose={() => setDeleting(null)}
      />
      {exportMounted ? (
        <Suspense fallback={null}>
          <ExportDialog
            nested={recentModal}
            request={exporting}
            onClose={() => setExporting(null)}
          />
        </Suspense>
      ) : null}
      {contextHolder}
    </div>
  );
};

export default ChatPage;
